import {
	Bandage,
	BatteryLow,
	Brain,
	CircleCheck,
	Droplets,
	Eye,
	Flame,
	Flower2,
	Moon,
	ShieldAlert,
	Sparkles,
	Thermometer,
	Utensils,
	Weight,
	Wind,
	Zap,
	type LucideIcon
} from "lucide-react"
import { Container } from "@/components/layout/Container"
import { Eyebrow } from "@/components/shared/Eyebrow"

interface TreatmentAboutProps {
	description: string
	solves: string[]
}

/**
 * Elige un icono según las palabras clave del síntoma. Los síntomas se repiten
 * mucho entre tratamientos, así que un mapa de palabras clave da iconos relevantes
 * sin tener que anotar cada uno a mano. Se evalúan en orden (lo específico antes
 * que lo general); si nada coincide, se usa un check.
 */
const SOLVE_ICONS: [RegExp, LucideIcon][] = [
	[/sueñ|insomnio|dormir|descans/i, Moon],
	[/nervios|ansiedad|estr[eé]s|concentr|memoria|temblor|rigidez|movilidad/i, Brain],
	[/respir|aire|tos|bronq|opresi[oó]n|pulmon|pecho/i, Wind],
	[/visi[oó]n|ojo|borrosa|luz|deslumbr/i, Eye],
	[/az[uú]car|glucosa|insulina|sed|micci[oó]n|circulaci[oó]n/i, Droplets],
	[/herida|[uú]lcera|quemadura|cort|raspad|ampoll|mordedura|cicatr/i, Bandage],
	[/piel|arruga|flacidez|escama|mancha|grano|espinilla|acn[eé]|p[uú]stula|n[oó]dulo/i, Sparkles],
	[/ardor|inflama|hinchaz|irrita|enrojec|pirosis|[aá]cida/i, Flame],
	[/fatiga|cansancio|debilidad|energ[ií]a/i, BatteryLow],
	[/peso|metabolismo|antojo|grasa/i, Weight],
	[/apetito|digest|est[oó]mag|estre[ñn]|evacu|abdominal|\bgas|hambre|n[aá]us|v[oó]mito|regurgit|tragar/i, Utensils],
	[/menstru|hormona|fertil|vaginal|menopa|er[eé]ctil|pr[oó]stata|bph/i, Flower2],
	[/verruga|infecci[oó]n|transmisi[oó]n|herpes|defensa|inmun/i, ShieldAlert],
	[/cabeza|fiebre/i, Thermometer],
	[/dolor|malestar/i, Zap]
]

function iconForSolve(text: string): LucideIcon {
	return SOLVE_ICONS.find(([pattern]) => pattern.test(text))?.[1] ?? CircleCheck
}

/**
 * Sección 2 (descripción): introducción del tratamiento y los problemas que
 * resuelve, cada uno con un icono (según su texto) dentro de una caja verde. En
 * móvil los ítems se apilan uno arriba del otro (icono + texto al lado, con
 * sangría izquierda); desde `sm` fluyen en fila centrada con `min-w` de 200px y
 * el texto bajo el icono.
 */
export function TreatmentAbout({ description, solves }: TreatmentAboutProps): React.JSX.Element {
	return (
		<section className="bg-background">
			<Container className="py-16 md:py-24">
				<div className="mx-auto max-w-3xl text-center">
					<Eyebrow align="center">Sobre el tratamiento</Eyebrow>
					<h2 className="mt-3 text-balance font-heading text-2xl font-semibold tracking-tight sm:text-3xl md:text-4xl">
						Salud desde el nivel celular
					</h2>
					<p className="mx-auto mt-5 text-pretty text-lg leading-relaxed text-muted-foreground">{description}</p>

					<div className="mt-12">
						<h3 className="font-heading text-2xl font-semibold tracking-tight sm:text-3xl">Problemas que resuelve</h3>
						<ul className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:flex-wrap sm:justify-center sm:gap-6">
							{solves.map(item => {
								const Icon = iconForSolve(item)
								return (
									<li
										key={item}
										className="flex w-full items-center gap-3 pl-8 sm:w-auto sm:min-w-[200px] sm:flex-col sm:gap-3 sm:pl-0 sm:text-center"
									>
										<span className="inline-flex size-12 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600">
											<Icon className="size-6" />
										</span>
										<span className="text-sm font-medium">{item}</span>
									</li>
								)
							})}
						</ul>
					</div>
				</div>
			</Container>
		</section>
	)
}
